import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Share, Plus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const InstallPrompt = () => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed in THIS SESSION only (not persisted across sessions)
    const dismissedThisSession = sessionStorage.getItem('installPromptDismissedSession');
    if (dismissedThisSession) {
      return;
    }

    // Listen for beforeinstallprompt (Android/Desktop Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show after a delay if not standalone
    if (iOS && !standalone) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        // Mark as installed permanently
        localStorage.setItem('appInstalled', 'true');
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Only dismiss for this session - will show again on next login/visit
    sessionStorage.setItem('installPromptDismissedSession', 'true');
  };

  // Don't show if already installed or marked as installed
  const appInstalled = localStorage.getItem('appInstalled') === 'true';
  if (isStandalone || appInstalled || !showPrompt) return null;

  const texts = {
    en: {
      title: 'Install IN App',
      description: 'Add to your home screen for the best experience',
      install: 'Install',
      iosTitle: 'Install IN App',
      iosStep1: 'Tap the share button',
      iosStep2: 'Scroll and tap "Add to Home Screen"',
      gotIt: 'Got it',
    },
    tr: {
      title: 'IN Uygulamasını Yükle',
      description: 'En iyi deneyim için ana ekranınıza ekleyin',
      install: 'Yükle',
      iosTitle: 'IN Uygulamasını Yükle',
      iosStep1: 'Paylaş butonuna dokunun',
      iosStep2: '"Ana Ekrana Ekle" seçeneğine dokunun',
      gotIt: 'Anladım',
    },
  };

  const t = texts[language] || texts.en;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-sm"
      >
        <div className="bg-[#0a0a0f] border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl backdrop-blur-xl">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-[#A1A1AA] hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>

          {isIOS ? (
            // iOS Instructions
            <div className="pr-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-lg text-[#F8F5F2]">{t.iosTitle}</h3>
              </div>
              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3 text-sm text-[#F8F5F2]">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center text-xs text-[#030305] font-bold">1</div>
                  <span className="flex items-center gap-2">
                    {t.iosStep1} <Share className="w-5 h-5 text-[#D4AF37]" />
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#F8F5F2]">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center text-xs text-[#030305] font-bold">2</div>
                  <span className="flex items-center gap-2">
                    {t.iosStep2} <Plus className="w-5 h-5 text-[#D4AF37]" />
                  </span>
                </div>
              </div>
              <p className="text-xs text-[#6B7280] mb-3 text-center">
                {language === 'tr' 
                  ? 'Safari\'nin alt kısmındaki paylaş simgesine (⬆️) dokunun' 
                  : 'Tap the share icon (⬆️) at the bottom of Safari'}
              </p>
              <button
                onClick={handleDismiss}
                className="w-full py-2.5 rounded-full bg-[#D4AF37] text-[#030305] font-semibold text-sm hover:bg-[#D4AF37]/90 transition-colors"
              >
                {t.gotIt}
              </button>
            </div>
          ) : (
            // Android/Desktop Install Button
            <div className="pr-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                  <Download className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-[#F8F5F2]">{t.title}</h3>
                  <p className="text-sm text-[#A1A1AA]">{t.description}</p>
                </div>
              </div>
              <button
                onClick={handleInstall}
                className="w-full mt-3 py-2.5 rounded-full bg-[#D4AF37] text-[#030305] font-semibold text-sm hover:bg-[#D4AF37]/90 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t.install}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
